import { TypedDocumentNode, gql } from '@apollo/client';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';

import { FlexCard } from '@sorare/marketplace/src/components/token/FlexCard';

import { GalleryCards_anyCard } from './__generated__/index.graphql';

const Cards = styled(Horizontal)`
  flex: 1;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  flex-wrap: wrap;
  width: 100%;
  padding: 0 var(--intermediate-unit);
  > * {
    max-width: min(200px, calc(33% - var(--intermediate-unit)));
  }
`;
const FakeCardRoot = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--double-unit);
  background: var(--c-nd-100);
  border: 1px solid var(--c-nd-300);
`;

const LineupCardsLayoutWrapper = styled.div`
  padding: 0 var(--intermediate-unit);
  flex: 1;
`;

const FakeCard = () => {
  return (
    <FakeCardRoot>
      <FontAwesomeIcon icon={faUser} />
    </FakeCardRoot>
  );
};

type Props = {
  cards: GalleryCards_anyCard[];
  hasHighlightedDeck: boolean;
  maxCardsToDisplay: number;
};

const CardsWrapper = ({
  children,
  hasHighlightedDeck,
  cardsCount,
  maxCardsToDisplay,
}: PropsWithChildren<{
  hasHighlightedDeck: boolean;
  cardsCount: number;
  maxCardsToDisplay: number;
}>) => {
  if (!hasHighlightedDeck || cardsCount >= maxCardsToDisplay) {
    return (
      <LineupCardsLayoutWrapper>
        <LineupCardsLayout fillHeightPercent={0.5}>
          {children}
        </LineupCardsLayout>
      </LineupCardsLayoutWrapper>
    );
  }
  return <Cards>{children}</Cards>;
};
export const GalleryCards = ({
  cards,
  hasHighlightedDeck,
  maxCardsToDisplay,
}: Props) => {
  const nbCardsMissing = Math.max(maxCardsToDisplay - cards.length, 0);

  return (
    <CardsWrapper
      maxCardsToDisplay={maxCardsToDisplay}
      hasHighlightedDeck={hasHighlightedDeck}
      cardsCount={cards.length}
    >
      {cards.map((card, index) => (
        <OnboardingHighlightWrapper
          key={card.slug}
          wrap={index === 0}
          highlightName="ManagerProfileGalleryCard"
        >
          <FlexCard withLink card={card} />
        </OnboardingHighlightWrapper>
      ))}
      {!hasHighlightedDeck &&
        [...Array(nbCardsMissing)].map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <FakeCard key={index} />
        ))}
    </CardsWrapper>
  );
};

GalleryCards.fragments = {
  anyCard: gql`
    fragment GalleryCards_anyCard on AnyCardInterface {
      slug
      ...FlexCard_anyCard
    }
    ${FlexCard.fragments.anyCard}
  ` as TypedDocumentNode<GalleryCards_anyCard>,
};
